/**
 * Centralized Module Registry
 * 
 * This is the single source of truth for all modules in the system.
 * All module definitions, labels, icons, and sub-modules are defined here.
 * 
 * Usage:
 * - Import MODULES array to get all module definitions
 * - Use module keys consistently across the codebase
 * - Add new modules here first, then reference elsewhere
 */

export interface SubModule {
  key: string;
  label: string;
}

export interface Module {
  key: string;
  label: string;
  icon?: string;
  subModules?: SubModule[];
}

/**
 * Complete module registry
 * 
 * All modules available in the system, organized by category.
 * Each module has a unique key, display label, optional icon, and optional sub-modules.
 */
export const MODULES: Module[] = [
  // Core Business Modules
  {
    key: 'bill_collection',
    label: 'Bill Collection',
    icon: 'CreditCard',
    subModules: [
      { key: 'queue', label: 'Queue Management' },
      { key: 'workflows', label: 'Workflows' },
      { key: 'payment_plans', label: 'Payment Plans' },
    ],
  },
  {
    key: 'bill_processing',
    label: 'Bill Processing',
    icon: 'FileText',
    subModules: [
      { key: 'processing', label: 'Processing' },
      { key: 'validation', label: 'Validation' },
      { key: 'export', label: 'Export' },
    ],
  },
  {
    key: 'onboarding',
    label: 'Customer Onboarding',
    icon: 'Users',
    subModules: [
      { key: 'customers', label: 'Customers' },
      { key: 'partners', label: 'Partners' },
      { key: 'suppliers', label: 'Suppliers' },
      { key: 'sites', label: 'Sites' },
      { key: 'accounts', label: 'Accounts' },
      { key: 'meters', label: 'Meters' },
    ],
  },

  // AI & Automation
  {
    key: 'ai_parser',
    label: 'AI Parser',
    icon: 'Brain',
    subModules: [
      { key: 'parsing', label: 'Document Parsing' },
      { key: 'extraction', label: 'Data Extraction' },
      { key: 'validation', label: 'AI Validation' },
    ],
  },
  {
    key: 'ai_agents',
    label: 'AI Agents',
    icon: 'Bot',
    subModules: [
      { key: 'agents', label: 'Agent Management' },
      { key: 'automation', label: 'Automation' },
      { key: 'workflows', label: 'AI Workflows' },
    ],
  },
  {
    key: 'automation',
    label: 'Automation',
    icon: 'Zap',
    subModules: [
      { key: 'workflows', label: 'Workflows' },
      { key: 'triggers', label: 'Triggers' },
      { key: 'schedules', label: 'Schedules' },
    ],
  },

  // Reports & Analytics
  {
    key: 'reporting',
    label: 'Reports & Analytics',
    icon: 'BarChart3',
    subModules: [
      { key: 'reports', label: 'Reports' },
      { key: 'dashboards', label: 'Dashboards' },
      { key: 'health_monitor', label: 'Health Monitor' },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: 'TrendingUp',
    subModules: [
      { key: 'insights', label: 'Insights' },
      { key: 'metrics', label: 'Metrics' },
      { key: 'visualizations', label: 'Visualizations' },
    ],
  },
  {
    key: 'enterprise_analytics',
    label: 'Enterprise Analytics',
    icon: 'ChartPie',
    subModules: [
      { key: 'advanced_analytics', label: 'Advanced Analytics' },
      { key: 'predictive', label: 'Predictive Analytics' },
      { key: 'custom_reports', label: 'Custom Reports' },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    icon: 'Shield',
    subModules: [
      { key: 'monitoring', label: 'Compliance Monitoring' },
      { key: 'reporting', label: 'Compliance Reporting' },
      { key: 'audits', label: 'Audits' },
    ],
  },

  // Integration & API
  {
    key: 'integrations',
    label: 'Integrations',
    icon: 'Link',
    subModules: [
      { key: 'third_party', label: 'Third-Party Services' },
      { key: 'webhooks', label: 'Webhooks' },
      { key: 'connectors', label: 'Connectors' },
    ],
  },
  {
    key: 'api',
    label: 'API Access',
    icon: 'Code',
    subModules: [
      { key: 'api_keys', label: 'API Keys' },
      { key: 'documentation', label: 'Documentation' },
      { key: 'rate_limits', label: 'Rate Limits' },
    ],
  },

  // Storage & Support
  {
    key: 'storage',
    label: 'Enhanced Storage',
    icon: 'Database',
    subModules: [
      { key: 'file_storage', label: 'File Storage' },
      { key: 'backup', label: 'Backup' },
      { key: 'archival', label: 'Archival' },
    ],
  },
  {
    key: 'support',
    label: 'Priority Support',
    icon: 'HeadphonesIcon',
    subModules: [
      { key: 'tickets', label: 'Support Tickets' },
      { key: 'chat', label: 'Live Chat' },
      { key: 'documentation', label: 'Documentation' },
    ],
  },

  // Workspace Administration
  {
    key: 'workspace_admin',
    label: 'Workspace Admin',
    icon: 'Settings',
    subModules: [
      { key: 'users', label: 'User Management' },
      { key: 'roles', label: 'Role Management' },
      { key: 'modules', label: 'Module Management' },
      { key: 'settings', label: 'Workspace Settings' },
      { key: 'branding', label: 'Branding' },
      { key: 'audit_logs', label: 'Audit Logs' },
    ],
  },
  {
    key: 'trial_management',
    label: 'Trial Management',
    icon: 'Timer',
    subModules: [
      { key: 'trial_status', label: 'Trial Status' },
      { key: 'upgrade', label: 'Upgrade' },
      { key: 'billing', label: 'Billing' },
    ],
  },
  {
    key: 'bulk_operations',
    label: 'Bulk Operations',
    icon: 'Upload',
    subModules: [
      { key: 'import', label: 'Bulk Import' },
      { key: 'export', label: 'Bulk Export' },
      { key: 'processing', label: 'Bulk Processing' },
    ],
  },
  {
    key: 'audit_logs',
    label: 'Audit Logs',
    icon: 'FileText',
    subModules: [
      { key: 'activity', label: 'Activity Logs' },
      { key: 'security', label: 'Security Logs' },
      { key: 'compliance', label: 'Compliance Logs' },
    ],
  },
  {
    key: 'security_monitoring',
    label: 'Security Monitoring',
    icon: 'Shield',
    subModules: [
      { key: 'monitoring', label: 'Security Monitoring' },
      { key: 'alerts', label: 'Security Alerts' },
      { key: 'incidents', label: 'Security Incidents' },
    ],
  },
  {
    key: 'role_management',
    label: 'Role Management',
    icon: 'Users',
    subModules: [
      { key: 'roles', label: 'Roles' },
      { key: 'permissions', label: 'Permissions' },
      { key: 'assignments', label: 'Role Assignments' },
    ],
  },
  {
    key: 'multi_tenant',
    label: 'Multi-Tenant',
    icon: 'Building',
    subModules: [
      { key: 'workspaces', label: 'Workspaces' },
      { key: 'isolation', label: 'Data Isolation' },
      { key: 'cross_workspace', label: 'Cross-Workspace' },
    ],
  },

  // Special Modules
  {
    key: 'tum',
    label: 'Tenant Utility Manager',
    icon: 'Building',
    subModules: [
      { key: 'properties', label: 'Properties' },
      { key: 'tenants', label: 'Tenants' },
      { key: 'utilities', label: 'Utilities' },
      { key: 'billing', label: 'Billing' },
    ],
  },
];

/**
 * Get a module by its key
 */
export function getModule(key: string): Module | undefined {
  return MODULES.find((module) => module.key === key);
}

/**
 * Get all module keys
 */
export function getModuleKeys(): string[] {
  return MODULES.map((module) => module.key);
}

/**
 * Get all sub-module keys for a given module
 */
export function getSubModuleKeys(moduleKey: string): string[] {
  const module = getModule(moduleKey);
  return module?.subModules?.map((sub) => sub.key) || [];
}

/**
 * Check if a module exists
 */
export function hasModule(key: string): boolean {
  return MODULES.some((module) => module.key === key);
}

/**
 * Get module label by key
 */
export function getModuleLabel(key: string): string {
  const module = getModule(key);
  return module?.label || key;
}

/**
 * Get sub-module label by module and sub-module keys
 */
export function getSubModuleLabel(moduleKey: string, subModuleKey: string): string {
  const module = getModule(moduleKey);
  const subModule = module?.subModules?.find((sub) => sub.key === subModuleKey);
  return subModule?.label || subModuleKey;
}

